import { ChangeDetectionStrategy, Component, EventEmitter, Output, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { Avatar } from '../avatar/avatar';
import { AppStatusComponent } from '../app-status/app-status.component';
import { LanguageService } from '../services/language.service';
import { type SupportedLanguage } from '@zag/core/common';
import { ThemeService } from '../services/theme.service';

/**
 * User profile data for the sidebar profile component.
 */
export interface ProfileUser {
  name: string;
  email?: string;
  avatarUrl?: string;
}

/**
 * A reusable panel containing user preferences and profile controls:
 * - User info (Avatar + Name)
 * - Navigation links (My Profile, Notifications)
 * - Theme switcher (horizontally stacked)
 * - Language switcher (positioned last)
 * - Logout button
 *
 * Used in both the profile dropdown and the profile mobile dialog.
 */
@Component({
  selector: 'zag-user-preferences-panel',
  imports: [RouterLink, TranslatePipe, ZagButton, ZagIcon, Avatar, AppStatusComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="user-preferences-panel">
      <!-- User Info Header (optional, usually for mobile/dialogs) -->
      @if (showHeader() && user()) {
        <div class="user-panel__header" [class.user-panel__header--link]="!!profileRoute()">
          @if (profileRoute()) {
            <a [routerLink]="profileRoute()" (click)="onLinkClick()" class="user-panel__header-link">
              <zag-avatar [name]="user()!.name" [src]="user()!.avatarUrl" size="lg" />
              <div class="user-panel__header-info">
                <span class="user-panel__header-name">{{ user()!.name }}</span>
                @if (user()!.email) {
                  <span class="user-panel__header-email">{{ user()!.email }}</span>
                }
              </div>
            </a>
          } @else {
            <div class="user-panel__header-content">
              <zag-avatar [name]="user()!.name" [src]="user()!.avatarUrl" size="lg" />
              <div class="user-panel__header-info">
                <span class="user-panel__header-name">{{ user()!.name }}</span>
                @if (user()!.email) {
                  <span class="user-panel__header-email">{{ user()!.email }}</span>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- Menu Items Section — only when at least one link renders, otherwise the padded empty div shows as a stripe -->
      @if ((!showHeader() && profileRoute()) || showNotifications()) {
        <div class="user-panel__section">
          <!-- My Profile Link (if not in header) -->
          @if (!showHeader() && profileRoute()) {
            <a [routerLink]="profileRoute()" (click)="onLinkClick()" class="user-panel__menu-item">
              <span zagIcon="manage_accounts"></span>
              <span class="user-panel__menu-label">{{ 'navigation.myProfile' | translate }}</span>
            </a>
          }

          <!-- Notifications Link (Optional) -->
          @if (showNotifications()) {
            <a routerLink="/notifications" (click)="onLinkClick()" class="user-panel__menu-item">
              <span zagIcon="notifications"></span>
              <span class="user-panel__menu-label">{{ 'navigation.notifications' | translate }}</span>
              @if (unreadCount() > 0) {
                <span class="user-panel__unread-badge">
                  {{ unreadCount() > 99 ? '99+' : unreadCount() }}
                </span>
              }
            </a>
          }
        </div>
      }

      <!-- Theme Switcher Section -->
      <div class="user-panel__section">
        <div class="user-panel__theme-switcher">
          <button
            type="button"
            class="user-panel__theme-option"
            [class.user-panel__theme-option--active]="currentTheme() === 'light'"
            (click)="currentTheme() !== 'light' && onThemeToggle()"
            [attr.aria-label]="'common.lightMode' | translate"
          >
            <span zagIcon="light_mode"></span>
            <span>{{ 'common.lightMode' | translate }}</span>
          </button>
          <button
            type="button"
            class="user-panel__theme-option"
            [class.user-panel__theme-option--active]="currentTheme() === 'dark'"
            (click)="currentTheme() !== 'dark' && onThemeToggle()"
            [attr.aria-label]="'common.darkMode' | translate"
          >
            <span zagIcon="dark_mode"></span>
            <span>{{ 'common.darkMode' | translate }}</span>
          </button>
        </div>
      </div>

      <!-- Language Switcher Section -->
      <div class="user-panel__section user-panel__section--language">
        <div class="user-panel__language-header">
          <span zagIcon="translate" class="user-panel__language-icon"></span>
        </div>
        <div class="user-panel__language-options">
          @for (lang of supportedLanguages; track lang) {
            <button
              type="button"
              class="user-panel__language-btn"
              [class.user-panel__language-btn--active]="currentLanguage() === lang"
              (click)="onLanguageChange(lang)"
            >
              {{ lang.toUpperCase() }}
            </button>
            @if (!$last) {
              <span class="user-panel__language-separator">|</span>
            }
          }
        </div>
      </div>

      <!-- App Status Section -->
      <div class="user-panel__section user-panel__section--status">
        <zag-app-status (statusClick)="onVersionInfoClick()" />
      </div>

      <!-- Logout Section -->
      <div class="user-panel__section user-panel__section--logout">
        <button zagButton="outlined" zagType="error" type="button" class="user-panel__logout-btn" (click)="onLogoutClick()">
          <span zagIcon="logout"></span>
          {{ 'auth.logout' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }

      .user-preferences-panel {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      /* User Panel Header */
      .user-panel__header {
        padding: var(--spacing-md) var(--spacing-lg);
        border-bottom: 1px solid var(--border-color-default);
      }

      .user-panel__header-link {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        text-decoration: none;
        color: inherit;
        transition: background-color var(--transition-duration-fast);
        border-radius: var(--border-radius-md);

        &:hover {
          background-color: var(--surface-color-hover);
        }
      }

      .user-panel__header-content {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
      }

      .user-panel__header-info {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      .user-panel__header-name {
        font-weight: 600;
        font-size: var(--font-size-md);
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-panel__header-email {
        font-size: var(--font-size-xs);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Sections */
      .user-panel__section {
        padding: var(--spacing-sm) var(--spacing-md);
        border-bottom: 1px solid var(--border-color-default);

        &:last-child {
          border-bottom: none;
        }
      }

      .user-panel__section--status {
        display: flex;
        justify-content: center;
      }

      .user-panel__section--logout {
        padding: var(--spacing-sm);
        background: var(--color-error-subtle, rgba(239, 83, 80, 0.05));
        border-top: 1px solid var(--color-error-muted, rgba(239, 83, 80, 0.2));
      }

      .user-panel__section--language {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-md);
        padding: var(--spacing-md);
      }

      /* Menu Items */
      .user-panel__menu-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        padding: var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-primary);
        font-size: var(--font-size-sm);
        cursor: pointer;
        text-align: left;
        text-decoration: none;
        transition: background-color var(--transition-duration-fast);
        box-sizing: border-box;

        &:hover {
          background-color: var(--surface-color-hover);
          color: var(--color-primary);
        }

        [zagIcon] {
          font-size: 20px;
          color: var(--text-color-secondary);
        }
      }

      .user-panel__menu-label {
        flex: 1;
      }

      .user-panel__unread-badge {
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        background: var(--color-error, #ef5350);
        color: var(--color-neutral-0, #fff);
        font-size: 10px;
        font-weight: 700;
        line-height: 18px;
        text-align: center;
        border-radius: 9px;
      }

      /* Theme Switcher */
      .user-panel__theme-switcher {
        display: flex;
        align-items: center;
        gap: 0;
        padding: var(--spacing-xs);
        background: var(--surface-color-secondary);
        border-radius: var(--border-radius-md);
        margin: var(--spacing-xs) 0;
        justify-content: center;
        flex-wrap: nowrap;
      }

      .user-panel__theme-option {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: var(--spacing-sm);
        flex: 1;
        padding: var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        font-size: var(--font-size-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-duration-fast);

        &:hover {
          color: var(--text-color-primary);
        }

        [zagIcon] {
          font-size: 18px;
        }

        &.user-panel__theme-option--active {
          background: var(--background-color-elevated);
          color: var(--text-color-primary);
          box-shadow: var(--shadow-sm);
          cursor: default;
        }
      }

      /* Language Switcher */
      .user-panel__language-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
      }

      .user-panel__language-icon {
        font-size: 20px;
        color: var(--text-color-secondary);
        padding-left: var(--spacing-sm);
      }

      .user-panel__language-label {
        font-size: var(--font-size-sm);
        font-weight: 500;
        color: var(--text-color-secondary);
      }

      .user-panel__language-options {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
      }

      .user-panel__language-btn {
        padding: var(--spacing-xs) var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-sm);
        color: var(--color-primary);
        font-size: var(--font-size-sm);
        font-weight: 400;
        cursor: pointer;
        transition: all var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &.user-panel__language-btn--active {
          font-weight: 700;
          color: var(--text-color-primary);
          cursor: default;

          &:hover {
            background-color: transparent;
          }
        }
      }

      .user-panel__language-separator {
        color: var(--text-color-secondary);
        user-select: none;
      }

      /* Logout Button */
      .user-panel__logout-btn {
        width: 100%;
      }
    `,
  ],
})
export class UserPreferencesPanel {
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);

  /** User data to display */
  readonly user = input<ProfileUser | null>(null);

  /** Whether to show the header with user info */
  readonly showHeader = input<boolean>(false);

  /** Whether to show the notifications link */
  readonly showNotifications = input<boolean>(false);

  /** Unread notification count */
  readonly unreadCount = input<number>(0);

  /** Route to the profile page (if provided, "My Profile" link will be shown) */
  readonly profileRoute = input<string | null>(null);

  /** Emitted when a navigation link is clicked */
  readonly linkClick = output<void>();

  /** Emitted when the logout button is clicked */
  readonly logout = output<void>();

  /** Emitted when the app version is activated */
  @Output() readonly versionInfoClick = new EventEmitter<void>();

  // Expose service state
  protected readonly currentLanguage = this.languageService.currentLanguage;
  protected readonly supportedLanguages = this.languageService.getSupportedLanguages();
  protected readonly currentTheme = this.themeService.currentTheme;

  protected onLanguageChange(lang: SupportedLanguage): void {
    this.languageService.setLanguage(lang);
  }

  protected onThemeToggle(): void {
    this.themeService.toggleTheme();
  }

  protected onLogoutClick(): void {
    this.logout.emit();
  }

  protected onVersionInfoClick(): void {
    this.versionInfoClick.emit();
  }

  protected onLinkClick(): void {
    this.linkClick.emit();
  }
}
